import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { endpoints, baseUrl } from '../../api/api';
import { AdminBook } from './types';
import AdminHeader from './components/AdminHeader';
import BooksTable from './components/BooksTable';
import ConversionFunnel from './components/ConversionFunnel';
import WizardDropoff from './components/WizardDropoff';

interface CollapsiblePanelProps {
  title: string;
  storageKey: string;
  children: React.ReactNode;
}

const CollapsiblePanel: React.FC<CollapsiblePanelProps> = ({ title, storageKey, children }) => {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(storageKey) !== 'false'; } catch { return true; }
  });

  const toggle = () => {
    setOpen(prev => {
      const next = !prev;
      try { localStorage.setItem(storageKey, String(next)); } catch {}
      return next;
    });
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-100 transition-colors"
      >
        <span className="text-sm font-semibold text-gray-700">{title}</span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div className="border-t border-gray-200">{children}</div>}
    </div>
  );
};

const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdParam   = searchParams.get('userId')   || '';
  const userNameParam = searchParams.get('userName')  || '';

  const statusFilter = searchParams.get('status') || '';
  const currentPage  = parseInt(searchParams.get('page') || '1', 10);

  const [books, setBooks] = useState<AdminBook[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchBooks = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (userIdParam)  params.set('userId', userIdParam);
      params.set('page', String(currentPage));
      params.set('limit', '20');

      const response = await axios.get(
        `${baseUrl}${endpoints.getAllBooks}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBooks(response.data.books || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch books:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, currentPage, userIdParam]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData.role !== 'admin') {
          navigate('/');
          return;
        }
      } catch {
        navigate('/');
        return;
      }
    }
    fetchBooks();
  }, [fetchBooks, navigate]);

  const handleViewDetails = (bookId: string) => {
    navigate(`/admin/book/${bookId}`);
  };

  const handleDeleteBook = async (bookId: string) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(
        `${baseUrl}${endpoints.adminDeleteBook.replace(':id', bookId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBooks((prev) => prev.filter((b) => b._id !== bookId));
      setTotalCount((prev) => prev - 1);
    } catch (error) {
      console.error('Failed to delete book:', error);
      alert('Failed to delete book. Please try again.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (e.target.value) next.set('status', e.target.value); else next.delete('status');
      next.delete('page');
      return next;
    });
  };

  const clearUserFilter = () => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('userId');
      next.delete('userName');
      next.delete('page');
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader onLogout={handleLogout} />
      <main className="px-4 py-8 space-y-6">
        <CollapsiblePanel title="Conversion Funnel" storageKey="admin_panel_funnel">
          <ConversionFunnel />
        </CollapsiblePanel>
        <CollapsiblePanel title="Wizard Drop-off" storageKey="admin_panel_dropoff">
          <div className="flex gap-6">
            <WizardDropoff />
          </div>
        </CollapsiblePanel>
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900">
                {userIdParam ? 'Books' : 'All Books'} ({loading ? '…' : totalCount})
              </h2>
              {userIdParam && (
                <span className="flex items-center gap-1.5 pl-3 pr-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-full">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {decodeURIComponent(userNameParam) || 'User'}
                  <button
                    onClick={clearUserFilter}
                    className="ml-0.5 text-blue-400 hover:text-blue-700 font-bold leading-none"
                    title="Clear user filter"
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          {loading
            ? <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" /></div>
            : <BooksTable books={books} onViewDetails={handleViewDetails} onDelete={handleDeleteBook} />
          }
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-500">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('page', String(Math.max(1, currentPage - 1))); return n; })}
                  disabled={currentPage <= 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('page', String(Math.min(totalPages, currentPage + 1))); return n; })}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
